import { ACCEPTED_IMAGE_MIME_TYPES, ACCEPTED_IMAGE_TYPES, MAX_IMAGE_SIZE } from '@/constants'
import {
  arraySchema,
  createMultipleFilesSchema,
  idSchema,
  numberSchema,
  projectFileSchema,
  stringSchema,
  unionSchema,
  objectSchema,
  zCustomIssue,
  emailWithEmptySchema,
} from '@/schemas'
import { isNumber } from '@/utils'

const addressSchemaBasic = objectSchema({
  city: stringSchema.nullable().optional(),
  country: stringSchema.nullable().optional(),
  postal_code: stringSchema.nullable().optional(),
  state: stringSchema.nullable().optional(),
  street: stringSchema.nullable().optional(),
})

const addressSchema = addressSchemaBasic.extend({
  full_address: stringSchema.optional(),
})

const patientBasicSchema = objectSchema({
  address: addressSchemaBasic.nullable().optional(),
  birthday: stringSchema.optional(),
  category: stringSchema.optional(),
  cell_phone_number: stringSchema.optional(),
  chosen_name: stringSchema.optional(),
  email: emailWithEmptySchema.optional(),
  ethnicity: stringSchema.optional(),
  first_name: stringSchema.optional(),
  gender: stringSchema.optional(),
  home_phone_number: stringSchema.optional(),
  identity_gender: stringSchema.optional(),
  last_name: stringSchema.optional(),
  middle_name: stringSchema.optional(),
  status: stringSchema.optional(),
  website_id: unionSchema([numberSchema, stringSchema]),
})

export const patientReviewSchema = objectSchema({
  id: stringSchema,
  rating: numberSchema,
  review: stringSchema.optional(),
  social_review_url: stringSchema.optional(),
  created_at: stringSchema,
  updated_at: stringSchema,
  client_ulid: stringSchema,
  website_id: unionSchema([numberSchema, stringSchema]),
})

export const patientSchema = patientBasicSchema.extend({
  id: idSchema,
  address: addressSchema.nullable(),
  age: numberSchema.nullable().optional(),
  avatar: arraySchema(projectFileSchema).optional(),
  cell_phone_string: stringSchema.optional(),
  home_phone_string: stringSchema.optional(),
  verified_phone: stringSchema.optional(),
  verified_email: stringSchema.optional(),
  name: stringSchema.optional(),
  phone_verified: numberSchema.optional(),
  review_page_url: stringSchema.optional(),
  created_at: stringSchema,
  updated_at: stringSchema,
  latest_activity: stringSchema.optional(),
  last_review: patientReviewSchema.optional(),
  patient_reviews: arraySchema(patientReviewSchema).optional(),
  mailchimp: objectSchema({
    subscription_status: stringSchema,
  }).optional(),
})

export const patientFormSchema = patientBasicSchema
  .extend({
    avatar: createMultipleFilesSchema({
      maxSize: MAX_IMAGE_SIZE,
      acceptedMimeTypes: ACCEPTED_IMAGE_MIME_TYPES,
      acceptedTypesDescription: ACCEPTED_IMAGE_TYPES,
    }),
  })
  .superRefine((data, ctx) => {
    if (!data.email && !data.cell_phone_number) {
      ctx.issues.push({
        code: zCustomIssue,
        input: data.email,
        path: ['email'],
        message: 'Either email or cell phone number must be provided',
      })
      ctx.issues.push({
        code: zCustomIssue,
        input: data.cell_phone_number,
        path: ['cell_phone_number'],
        message: 'Either email or cell phone number must be provided',
      })
    }
  })

export const journeySchema = objectSchema({
  id: idSchema.optional(),
  coordinator_id: stringSchema.optional(),
  provider_id: stringSchema.optional(),
  procedures: arraySchema(stringSchema).optional(),
  patient_id: stringSchema.min(1, 'Patient is required'),
  status: stringSchema,
  minimum_price: unionSchema([numberSchema.min(0, 'Min price must be at least 0'), stringSchema])
    .optional()
    .nullable(),
  maximum_price: unionSchema([numberSchema.min(0, 'Max price must be at least 0'), stringSchema])
    .optional()
    .nullable(),
  notes: stringSchema.optional().nullable(),
  style: objectSchema({
    icon: stringSchema.optional(),
    background_color: stringSchema.optional(),
    text_color: stringSchema.optional(),
    border_color: stringSchema.optional(),
  }).optional(),
  control_by: stringSchema.optional(),
}).superRefine((data, ctx) => {
  const { minimum_price, maximum_price } = data
  const min = typeof minimum_price === 'string' && minimum_price ? Number(minimum_price) : minimum_price
  const max = typeof maximum_price === 'string' && maximum_price ? Number(maximum_price) : maximum_price

  if (isNumber(min) && isNumber(max) && max < min) {
    ctx.issues.push({
      code: zCustomIssue,
      input: maximum_price,
      path: ['maximum_price'],
      message: 'Max price must be at least min price',
    })
  }
})
